@layer refine.base {
  /* ========================================
       Base Styles & Defaults
       ======================================== */

  :host {
    --_font-size: var(--input-font-size, var(--text-sm));
    --_gap: var(--input-gap, var(--size-2));
    --_field-height: var(--input-height, var(--size-10));
    --_padding: var(--input-padding, var(--size-1-5) var(--size-3));
    --_label-block-inset: var(--input-label-block-inset, var(--size-2));
    --_label-inset: var(--input-label-inset, var(--size-3));
    --_radius: var(--input-radius, var(--rounded-lg));
    --_placeholder: var(--input-placeholder-color, var(--color-contrast-500));
    --_bg: var(--input-bg, var(--color-contrast-100));
    --_border-color: var(--input-border-color, var(--color-contrast-300));

    display: inline-flex;
    flex-direction: column;
    align-items: stretch;
    min-width: var(--input-min-width, 12rem);
  }

  .input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
  }

  .field {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: stretch;
    justify-content: center;
    height: var(--_field-height);
    min-height: var(--_field-height);
    padding: var(--_padding);
    background: var(--_bg);
    border: var(--border) solid var(--_border-color);
    border-radius: var(--_radius);
    box-shadow: var(--shadow-2xs);
    transition: var(
      --_motion-transition,
      background var(--transition-fast),
      backdrop-filter var(--transition-fast),
      border-color var(--transition-fast),
      box-shadow var(--transition-fast)
    );
  }

  /* Expand height to fit inset label + input row */
  .input-wrapper:has(.label:not([hidden])) .field {
    height: auto;
  }

  /* Outside placement: label is above the field (natural flow) */

  /* Inset placement (default): label appears inside the field border */
  :host(:not([label-placement='outside'])) .input-wrapper {
    position: relative;
  }

  :host(:not([label-placement='outside'])) .label {
    position: absolute;
    inset-block-start: var(--_label-block-inset);
    inset-inline-start: var(--_label-inset);
    z-index: 1;
    pointer-events: none;
  }

  :host(:not([label-placement='outside'])) .input-wrapper:has(.label:not([hidden])) .field {
    padding-block-start: calc(var(--_label-block-inset) + 1lh + 2px);
  }

  .input-row {
    display: flex;
    flex: 1;
    gap: var(--_gap);
    align-items: center;
  }

  /* ========================================
       Label Styles
       ======================================== */

  .label,
  label.label {
    margin-bottom: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    line-height: var(--leading-tight);
    color: var(--color-contrast-500);
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    transition: var(--_motion-transition, color var(--transition-fast));
  }

  /* Outside placement: label moves outside the field via CSS */
  :host([label-placement='outside']) .label,
  :host([label-placement='outside']) label.label {
    order: -1;
    font-size: var(--text-xs);
    line-height: var(--leading-loose);
  }

  /* ========================================
       Helper Text
       ======================================== */

  .helper-text {
    padding-inline: 2px;
    margin-top: var(--size-1-5);
    font-size: var(--text-xs);
    line-height: var(--leading-tight);
    color: color-mix(in oklch, var(--color-contrast-500) 80%, var(--color-canvas));
    overflow-wrap: anywhere;
  }

  .helper-text[hidden] {
    display: none;
  }

  /* ========================================
       Slotted Prefix/Suffix Icons
       ======================================== */

  ::slotted(svg) {
    flex-shrink: 0;
    width: var(--_icon-size, var(--leading-6));
    height: var(--_icon-size, var(--leading-6));
  }
}

@layer refine.overrides {
  /* ========================================
       Input Element
       ======================================== */

  input {
    flex: 1;
    min-width: 0;
    font: inherit;
    font-size: var(--_font-size);
    line-height: var(--leading-normal);

    /* No all: unset needed - reset layer handles it */
    color: var(--color-contrast-900);
    outline: none;
    background: transparent;
    border: none;
  }

  input::placeholder {
    color: var(--_placeholder);
    transition: color var(--transition-fast);
  }

  input:focus-visible {
    outline: none;
  }

  /* ========================================
       Hover & Focus States
       ======================================== */

  :host(:not([disabled], [variant='bordered'], [variant='flat'])) .field:hover {
    border-color: var(--color-contrast-400);
  }

  :host(:not([disabled])) .field:focus-within .label {
    color: var(--_theme-focus);
  }

  :host(:not([disabled])) .field:focus-within ::slotted([slot='prefix']),
  :host(:not([disabled])) .field:focus-within ::slotted([slot='suffix']) {
    color: var(--_theme-focus);
  }

  /* ========================================
       Clear button
       ======================================== */

  .clear-btn {
    display: none;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: var(--size-5);
    min-width: var(--_touch-target);
    height: var(--size-5);
    min-height: var(--_touch-target);
    padding: 0;
    color: var(--color-contrast-500);
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: var(--rounded-sm);
    transition: var(--_motion-transition, color var(--transition-fast));
  }

  .clear-btn:hover {
    color: var(--color-contrast-900);
  }

  .clear-btn:focus-visible {
    outline: var(--border-2) solid var(--_theme-focus);
    outline-offset: var(--border);
  }

  :host([clearable]) .clear-btn {
    display: flex;
  }

  :host([clearable]:not([has-value])) .clear-btn {
    visibility: hidden;
    pointer-events: none;
  }

  /* ========================================
       Password visibility toggle
       ======================================== */

  .pwd-toggle-btn {
    display: none;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: var(--size-5);
    min-width: var(--_touch-target);
    height: var(--size-5);
    min-height: var(--_touch-target);
    padding: 0;
    color: var(--color-contrast-500);
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: var(--rounded-sm);
    transition: var(--_motion-transition, color var(--transition-fast));
  }

  .pwd-toggle-btn:hover {
    color: var(--color-contrast-900);
  }

  .pwd-toggle-btn:focus-visible {
    outline: var(--border-2) solid var(--_theme-focus);
    outline-offset: var(--border);
  }

  :host([type='password']) .pwd-toggle-btn {
    display: flex;
  }

  /* ========================================
       Character counter
       ======================================== */

  .char-counter {
    padding-inline: 2px;
    font-size: var(--text-xs);
    line-height: var(--leading-tight);
    color: var(--color-contrast-400);
    text-align: end;
    white-space: nowrap;
  }

  .char-counter[data-near-limit] {
    color: var(--color-warning, #f59e0b);
  }

  .char-counter[data-at-limit] {
    color: var(--color-error);
  }

  /* ========================================
       Error State
       ======================================== */

  :host([error]:not([error=''])) .field {
    border-color: var(--color-error);
  }

  :host([error]:not([error=''])) .label {
    color: var(--color-error);
  }

  .helper-text[role='alert'] {
    color: var(--color-error);
  }

  /* ========================================
       Success State (Signal 04)
       ======================================== */

  :host([success]) .field {
    border-color: var(--color-success);
  }

  /* ========================================
       Status Icon (Signals 03/04: error/success — never color alone)
       ======================================== */

  .status-icon {
    display: none;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: var(--size-5);
    height: var(--size-5);
  }

  :host([error]:not([error=''])) .status-icon,
  :host([success]) .status-icon {
    display: flex;
  }

  .status-icon[data-status='error'] {
    color: var(--color-error);
  }

  .status-icon[data-status='success'] {
    color: var(--color-success);
  }

  /* ========================================
       Loading Spinner (Signal 06)
       ======================================== */

  /* Keyframe must live in shadow DOM — @keyframes don't inherit across shadow roots */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .field-spinner {
    display: none;
    flex-shrink: 0;
    width: var(--size-4);
    height: var(--size-4);
    border: var(--border-2) solid var(--_theme-focus, currentcolor);
    border-inline-end-color: transparent;
    border-radius: 50%;
    animation: var(--_motion-animation, spin var(--transition-loader) infinite);
  }

  :host([loading]) .field-spinner {
    display: inline-block;
  }
}

/*
 * The solid/flat/bordered/outline/ghost variant switch (previously here) now lives in
 * `fieldVariantMixin({ container: '.field', tokenPrefix: 'input' })` in `input.ts`'s `styles`
 * array — shared with `ore-textarea`/`ore-message-composer` instead of hand-duplicated across
 * all three (see the mixin's own doc comment for what it covers and why). `ore-input`'s extra
 * `text` variant (below) is not part of that shared set, so it keeps its own block here.
 */
@layer refine.variants {
  /* Text — underline style */
  :host([variant='text']) {
    --_bg: var(--input-bg, transparent);
    --_border-color: var(--input-border-color, var(--color-contrast-300));
  }

  :host([variant='text']) .field {
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  :host([variant='text']) .field:focus-within {
    box-shadow: none;
  }

  /*
   * Focus/error/success `box-shadow` on `.field` — kept here in `refine.variants` (alongside
   * `fieldVariantMixin`'s rest-state `box-shadow` for solid/outline/ghost) rather than
   * `refine.overrides`, so normal specificity decides the winner. Cascade layers beat
   * specificity outright, so a `refine.overrides` box-shadow always lost to every variant's
   * unconditional rest-state `box-shadow`, focused/error/success or not — the same bug class
   * fixed for `ore-otp-input`'s focus ring.
   */
  :host(:not([disabled], [variant='text'], [variant='flat'])) .field:focus-within {
    background: var(--color-canvas);
    border-color: var(--_theme-focus);
    box-shadow: var(--_theme-shadow, var(--color-primary-focus-shadow));
  }

  :host([error]:not([error=''])) .field:focus-within {
    border-color: var(--color-error);
    box-shadow: var(--color-error-focus-shadow);
  }

  :host([success]) .field:focus-within {
    border-color: var(--color-success);
    box-shadow: var(--color-success-focus-shadow);
  }
}

@layer refine.utilities {
  /* Full width */
  :host([fullwidth]) {
    width: 100%;
  }
}

/* ========================================
     Disabled State (Signal 05) — intentionally unlayered
     ======================================== */

/*
 * The shared `disabledLoadingMixin` (pulled in via `fieldMixins`) dims `:host([disabled])`
 * with `opacity: 0.5` as bare, unlayered CSS. Cascade layers always lose to unlayered rules
 * regardless of source order or specificity, so overriding that from inside any `@layer`
 * block in this file (including `refine.overrides`) would be a no-op — this has to stay
 * unlayered too, to actually win.
 *
 * Per the field-state UX review: opacity-based dimming reads as a washed-out/loading look
 * and can mimic reduced-contrast content for low-vision users, rather than a clearly locked
 * field. Swapped for an explicit grayscale surface + `not-allowed` cursor (the cursor already
 * comes from the shared mixin, left untouched here).
 */
:host([disabled]) {
  opacity: 1;
}

:host([disabled]) .field {
  background: var(--color-contrast-100);
  border-color: var(--color-contrast-200);
  box-shadow: none;
}

:host([disabled]) input {
  color: var(--color-contrast-400);
}

:host([disabled]) input::placeholder {
  color: var(--color-contrast-300);
}

:host([disabled]) .label {
  color: var(--color-contrast-400);
}
